import {
  AreaType,
  ArtRrt,
  Method,
  Modality,
  Objective,
  PreFlightDocumentType,
  PreFlightPropertyType,
} from '@/api/preFlight/enumerations';
import { validateFiles } from '@/helpers';
import useErrorMessage from '@/hooks/useErrorMessage';
import useSnackbar from '@/hooks/useSnackbar';
import { ChangeEvent, useEffect, useState } from 'react';

import {
  StatusCode,
  convertRegistrationUf,
  ufAbbreviationToEnum,
} from '../../../../api/enumerations';
import {
  deletePreFlightDocument,
  uploadPreFlightDocument,
} from '../../../../api/preFlight';
import {
  PreFlightData,
  PreFlightDocumentData,
  UpdateWorkOrderParams,
} from '../../../../api/preFlight/types';
import { Constants } from '../../constants';

export interface UsePreFlightDetailProps {
  isEditing: boolean;
  setIsEditing: (value: boolean) => void;
  matriculaDoc: PreFlightDocumentData | undefined;
  requesterName: string;
  setRequesterName: (value: string) => void;
  requesterPhone: string;
  setRequesterPhone: (value: string) => void;
  requesterEmail: string;
  setRequesterEmail: (value: string) => void;
  objective: Objective;
  setObjective: (value: Objective) => void;
  propertyType: PreFlightPropertyType;
  setPropertyType: (value: PreFlightPropertyType) => void;
  areaType: AreaType;
  setAreaType: (value: AreaType) => void;
  modality: Modality;
  setModality: (value: Modality) => void;
  method: Method;
  setMethod: (value: Method) => void;
  street: string;
  setStreet: (value: string) => void;
  addressNumber: string;
  setAddressNumber: (value: string) => void;
  complement: string;
  setComplement: (value: string) => void;
  zipCode: string;
  setZipCode: (value: string) => void;
  district: string;
  setDistrict: (value: string) => void;
  city: string;
  setCity: (value: string) => void;
  addressState: number;
  setAddressState: (value: number) => void;
  deedNumber: string;
  setDeedNumber: (value: string) => void;
  artRrt: ArtRrt;
  setArtRrt: (value: ArtRrt) => void;
  technicalReview: boolean;
  setTechnicalReview: (value: boolean) => void;
  updatedDeed: boolean;
  setUpdatedDeed: (value: boolean) => void;
  companionName: string;
  setCompanionName: (value: string) => void;
  companionEmail: string;
  setCompanionEmail: (value: string) => void;
  companionPhone: string;
  setCompanionPhone: (value: string) => void;
  description: string;
  setDescription: (value: string) => void;
  fileName: string;
  setFileName: (value: string) => void;
  pinPlace: google.maps.LatLngLiteral;
  setPinPlace: (place: google.maps.LatLngLiteral) => void;
  isAddressEdited: boolean;
  setIsAddressEdited: (value: boolean) => void;
  hasSearched: boolean;
  setHasSearched: (value: boolean) => void;
  resetForm: () => void;
  handleSave: () => Promise<void>;
  downloadFile: (link: string) => void;
  handleReset: () => void;
  handleDeleteFile: (s3Key: string) => Promise<void>;
  handleFileUpload: (e: ChangeEvent<HTMLInputElement>) => Promise<void>;
  handleSubmitFile: () => Promise<void>;
}

interface UsePreFlightDetailParams {
  selectedPreflight: PreFlightData;
  handleUpdateWorkOrder: (
    id: number,
    params: UpdateWorkOrderParams
  ) => Promise<boolean>;
  onDocumentAdded: (doc: PreFlightDocumentData) => void;
  onDocumentDeleted: (reference: string) => void;
}

export function usePreFlightDetail({
  selectedPreflight,
  handleUpdateWorkOrder,
  onDocumentAdded,
  onDocumentDeleted,
}: UsePreFlightDetailParams): UsePreFlightDetailProps {
  const { requester, inspection } = selectedPreflight;

  const matriculaDoc = selectedPreflight.documents.find(
    (d) => d.type === PreFlightDocumentType.DEED
  );

  const [isEditing, setIsEditing] = useState(false);
  const [requesterName, setRequesterName] = useState('');
  const [requesterPhone, setRequesterPhone] = useState('');
  const [requesterEmail, setRequesterEmail] = useState('');
  const [objective, setObjective] = useState<Objective>(Objective.SELECT);
  const [propertyType, setPropertyType] = useState<PreFlightPropertyType>(
    PreFlightPropertyType.SELECT
  );
  const [areaType, setAreaType] = useState<AreaType>(AreaType.SELECT);
  const [modality, setModality] = useState<Modality>(Modality.SELECT);
  const [method, setMethod] = useState<Method>(Method.SELECT);
  const [street, setStreet] = useState('');
  const [addressNumber, setAddressNumber] = useState('');
  const [complement, setComplement] = useState('');
  const [zipCode, setZipCode] = useState('');
  const [district, setDistrict] = useState('');
  const [city, setCity] = useState('');
  const [addressState, setAddressState] = useState(0);
  const [deedNumber, setDeedNumber] = useState('');
  const [artRrt, setArtRrt] = useState<ArtRrt>(ArtRrt.SELECT);
  const [technicalReview, setTechnicalReview] = useState(false);
  const [updatedDeed, setUpdatedDeed] = useState(false);
  const [companionName, setCompanionName] = useState('');
  const [companionEmail, setCompanionEmail] = useState('');
  const [companionPhone, setCompanionPhone] = useState('');
  const [description, setDescription] = useState('');
  const [fileName, setFileName] = useState('');
  const [fileAttached, setFileAttached] = useState<File | string>();
  const [pinPlace, setPinPlace] = useState<google.maps.LatLngLiteral>({
    lat: -23.56162,
    lng: -46.65591,
  });
  const [isAddressEdited, setIsAddressEdited] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);

  const { handleSnackbar } = useSnackbar();
  const { getErrorMessage } = useErrorMessage();

  const resetForm = (): void => {
    const addr = selectedPreflight.address;
    setRequesterName(requester?.name ?? '');
    setRequesterPhone(requester?.phone ?? '');
    setRequesterEmail(requester?.email ?? '');
    setObjective(selectedPreflight.purpose.objective ?? Objective.SELECT);
    setPropertyType(
      selectedPreflight.purpose.property_type ?? PreFlightPropertyType.SELECT
    );
    setAreaType(selectedPreflight.purpose.area_type ?? AreaType.SELECT);
    setModality(selectedPreflight.appraisal.modality ?? Modality.SELECT);
    setMethod(selectedPreflight.appraisal.method ?? Method.SELECT);
    setStreet(addr.street ?? '');
    setAddressNumber(addr.number ?? '');
    setComplement(addr.complement ?? '');
    setZipCode(addr.zipcode ?? '');
    setDistrict(addr.neighborhood ?? '');
    setCity(addr.city ?? '');
    setAddressState(addr.uf ? ufAbbreviationToEnum[addr.uf] ?? 0 : 0);
    setPinPlace(
      addr.latitude && addr.longitude
        ? { lat: addr.latitude, lng: addr.longitude }
        : { lat: -23.56162, lng: -46.65591 }
    );
    setDeedNumber(selectedPreflight.registry.deed_number ?? '');
    setArtRrt(selectedPreflight.art_rrt ?? ArtRrt.SELECT);
    setTechnicalReview(selectedPreflight.addons.technical_review);
    setUpdatedDeed(selectedPreflight.addons.updated_deed);
    setCompanionName(inspection?.name ?? '');
    setCompanionEmail(inspection?.email ?? '');
    setCompanionPhone(inspection?.phone ?? '');
    setIsAddressEdited(false);
    setIsEditing(false);
  };

  useEffect(() => {
    resetForm();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedPreflight.id]);

  const validateForm = (): boolean => {
    const requiredStrings: [string, string][] = [
      [requesterName, Constants.requesterName],
      [requesterPhone, Constants.phone],
      [requesterEmail, Constants.email],
      [street, Constants.street],
      [addressNumber, Constants.number],
      [zipCode, Constants.zipCode],
      [district, Constants.district],
      [city, Constants.city],
      [deedNumber, Constants.registrationNumber],
      [companionName, Constants.name],
      [companionEmail, Constants.email],
      [companionPhone, Constants.phone],
    ];

    const emptyField = requiredStrings.find(([value]) => !value.trim());
    if (emptyField) {
      handleSnackbar(`${Constants.fillFieldMessage} ${emptyField[1]}`, true);
      return false;
    }

    if (objective === Objective.SELECT) {
      handleSnackbar(`${Constants.selectFieldMessage} ${Constants.goal}`, true);
      return false;
    }
    if (propertyType === PreFlightPropertyType.SELECT) {
      handleSnackbar(
        `${Constants.selectFieldMessage} ${Constants.propertyType}`,
        true
      );
      return false;
    }
    if (areaType === AreaType.SELECT) {
      handleSnackbar(
        `${Constants.selectFieldMessage} ${Constants.concept}`,
        true
      );
      return false;
    }
    if (modality === Modality.SELECT) {
      handleSnackbar(
        `${Constants.selectFieldMessage} ${Constants.modality}`,
        true
      );
      return false;
    }
    if (method === Method.SELECT) {
      handleSnackbar(
        `${Constants.selectFieldMessage} ${Constants.calculationType}`,
        true
      );
      return false;
    }
    if (!addressState) {
      handleSnackbar(
        `${Constants.selectFieldMessage} ${Constants.state}`,
        true
      );
      return false;
    }
    if (artRrt === ArtRrt.SELECT) {
      handleSnackbar(
        `${Constants.selectFieldMessage} ${Constants.artModality}`,
        true
      );
      return false;
    }

    return true;
  };

  const handleSave = async (): Promise<void> => {
    if (isAddressEdited) {
      handleSnackbar(Constants.searchMapBeforeSave, true);
      return;
    }
    if (!validateForm()) return;

    const success = await handleUpdateWorkOrder(selectedPreflight.id, {
      requester_name: requesterName,
      requester_phone: requesterPhone,
      requester_email: requesterEmail,
      objective,
      property_type: propertyType,
      area_type: areaType,
      modality,
      method,
      street,
      number: addressNumber,
      complement,
      zipcode: zipCode,
      neighborhood: district,
      city,
      uf: convertRegistrationUf(addressState),
      deed_number: deedNumber,
      art_rrt: artRrt,
      technical_review: technicalReview,
      updated_deed: updatedDeed,
      inspection_name: companionName,
      inspection_email: companionEmail,
      inspection_phone: companionPhone,
      latitude: pinPlace.lat,
      longitude: pinPlace.lng,
    });
    if (success) setIsEditing(false);
  };

  const downloadFile = (link: string): void => {
    const downloadLink = document.createElement('a');
    downloadLink.href = link;
    document.body.appendChild(downloadLink);
    downloadLink.click();
    document.body.removeChild(downloadLink);
  };

  const handleReset = (): void => {
    setDescription('');
    setFileName('');
    setFileAttached(undefined);
  };

  const handleDeleteFile = async (s3Key: string): Promise<void> => {
    try {
      const response = await deletePreFlightDocument(
        selectedPreflight.id,
        s3Key
      );

      if (response.detail.description)
        throw new Error(response.detail.description);

      if (response.detail.status_code !== StatusCode.OK)
        throw new Error(Constants.errorMessage);

      handleSnackbar(Constants.fileDeletedSuccess, false);
      onDocumentDeleted(s3Key);
    } catch (error) {
      handleSnackbar(getErrorMessage(error), true);
    }
  };

  const handleFileUpload = async (
    e: ChangeEvent<HTMLInputElement>
  ): Promise<void> => {
    if (!e.target.files?.item(0) || !validateFiles(e.target.files[0].type)) {
      handleSnackbar(Constants.fileFormatError, true);
      return;
    }
    const file = e.target.files[0];
    setFileName(file.name);
    setFileAttached(file);
  };

  const handleSubmitFile = async (): Promise<void> => {
    if (!fileAttached || typeof fileAttached === 'string') {
      handleSnackbar(Constants.addFileWarning, true);
      return;
    }

    const formData = new FormData();
    formData.append('file', fileAttached);
    formData.append('type', 'DEED');
    if (description) formData.append('description', description);

    try {
      const response = await uploadPreFlightDocument(
        selectedPreflight.id,
        formData
      );

      if (response.detail.description)
        throw new Error(response.detail.description);

      if (response.detail.status_code !== StatusCode.OK)
        throw new Error(Constants.errorMessage);

      handleSnackbar(Constants.fileAddedSuccess, false);
      if (response.data) onDocumentAdded(response.data);
      handleReset();
    } catch (error) {
      handleSnackbar(getErrorMessage(error), true);
    }
  };

  return {
    isEditing,
    setIsEditing,
    matriculaDoc,
    requesterName,
    setRequesterName,
    requesterPhone,
    setRequesterPhone,
    requesterEmail,
    setRequesterEmail,
    objective,
    setObjective,
    propertyType,
    setPropertyType,
    areaType,
    setAreaType,
    modality,
    setModality,
    method,
    setMethod,
    street,
    setStreet,
    addressNumber,
    setAddressNumber,
    complement,
    setComplement,
    zipCode,
    setZipCode,
    district,
    setDistrict,
    city,
    setCity,
    addressState,
    setAddressState,
    deedNumber,
    setDeedNumber,
    artRrt,
    setArtRrt,
    technicalReview,
    setTechnicalReview,
    updatedDeed,
    setUpdatedDeed,
    companionName,
    setCompanionName,
    companionEmail,
    setCompanionEmail,
    companionPhone,
    setCompanionPhone,
    description,
    setDescription,
    fileName,
    setFileName,
    pinPlace,
    setPinPlace,
    isAddressEdited,
    setIsAddressEdited,
    hasSearched,
    setHasSearched,
    resetForm,
    handleSave,
    downloadFile,
    handleReset,
    handleDeleteFile,
    handleFileUpload,
    handleSubmitFile,
  };
}
